import { useAtom } from 'jotai';
import useFetchJobChangeProcessesInfinite from '../api/useFetchJobChangeProcessesInfinite';
import JobChangeProcessList from '../components/list/JobChangeProcessList';
import JobChangeProcessSearchOption from '../components/search/JobChangeProcessSearchOption';
import {
  jobChangeProcessQueryAtom,
  jobChangeProcessSearchAtom,
  currentSelectjobChangeProcessAtom,
  jobChangeProcessScreenType,
} from '../atoms/jobChangeProcess';
import JobChangeProcessBoardWrapper from '../components/board/JobChangeProcessBoardWrapper';
import type { JobChangeProcess } from '../types/JobChangeProcess';
import JobChangeProcessSidebar from '../components/sidebar/JobChangeProcessDetailSidebar';
import clsx from 'clsx';
import { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import JobChangeProcessLogStatusUpdateModal from '../components/modal/JobChangeProcessLogStatusUpdateModal';
import useOnClickOutside from '../hooks/useOnClickOutside';
import Select from '@/components/Select';
import { Button } from '@/components/Button';
import { MdAutorenew } from 'react-icons/md';
import { ORDER_ITEMS } from '../constant';
import BackToSearchBar from '@/components/BackToSearchBar';
import { showErrorToast } from '../lib/toast';
import useActiveUsers from '../../active_users/hooks/useActiveUsers';
import useFetchIam from '../../api/useFetchIam';
import useUpdateCandidateProfile from '../../candidates/api/useUpdateCandidateProfile';

const UNASSIGNED_USER_ID = -1;

export default function JobChangeProcessTemplate() {
  const { data: activeUsersData } = useActiveUsers();
  const activeUsers = activeUsersData?.activeUsers ?? [];
  const { data: iam } = useFetchIam();
  const currentUserId = iam?.current_user?.id;
  const { mutateAsync: updateCandidate } = useUpdateCandidateProfile();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isOption, setIsOption] = useState(false);
  const [showTopBar, setShowTopBar] = useState(false);
  const [jobChangeProcessQuery, setJobChangeProcessQuery] = useAtom(jobChangeProcessQueryAtom);
  const [sharedSearch, setSharedSearch] = useAtom(jobChangeProcessSearchAtom);
  const [orderValue, setOrderValue] = useState('-updated_at');
  const [screenType, setScreenType] = useAtom(jobChangeProcessScreenType);

  // 退職者や別組織の担当者IDが localStorage に残ると、未選択表示のまま0件になる (#3663)
  useEffect(() => {
    if (!activeUsersData) return;
    const validIds = new Set([
      UNASSIGNED_USER_ID,
      ...activeUsersData.activeUsers.map((user) => user.id),
    ]);
    if (sharedSearch.organizationUserIds.every((id) => validIds.has(id))) return;
    setSharedSearch((prev) => ({
      ...prev,
      organizationUserIds: prev.organizationUserIds.filter((id) => validIds.has(id)),
    }));
  }, [activeUsersData, sharedSearch.organizationUserIds, setSharedSearch]);

  const searchParams = useMemo(
    () => ({
      step_range: jobChangeProcessQuery.stepRange,
      date_range: jobChangeProcessQuery.dateRange,
      schedule_statuses: jobChangeProcessQuery.scheduleStatuses,
      keyword: sharedSearch.keyword,
      organization_user_ids: sharedSearch.organizationUserIds,
      order: jobChangeProcessQuery.order,
      result: jobChangeProcessQuery.result,
    }),
    [jobChangeProcessQuery, sharedSearch]
  );

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    refetch: jobchangeProcessesRefetch,
  } = useFetchJobChangeProcessesInfinite(searchParams, { enabled: screenType === 'list' });

  const [currentSelectjobChangeProcess, setCurrentSelectjobChangeProcess] = useAtom(
    currentSelectjobChangeProcessAtom
  );

  const jobChangeProcesses = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.records);
  }, [data?.pages]);

  const [logStatusModalOpen, setLogStatusModalOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(sidebarRef, () => {
    setIsSidebarOpen(false);
    setIsVisible(false);
  });

  const handleScroll = useCallback(() => {
    if (!hasNextPage || isFetchingNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = document.documentElement;

    if (scrollTop + clientHeight >= scrollHeight - 500) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    const handleScrollForTopBar = () => {
      const scrollPosition = window.scrollY;
      setShowTopBar(scrollPosition >= 16);
    };

    window.addEventListener('scroll', handleScrollForTopBar);
    handleScrollForTopBar();

    return () => {
      window.removeEventListener('scroll', handleScrollForTopBar);
    };
  }, []);

  // ボードビューは列ごとに独自のページ送りを持つため、リストの無限スクロールは走らせない
  useEffect(() => {
    if (screenType !== 'list') return;

    let timeoutId: NodeJS.Timeout;
    const debouncedScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleScroll, 100);
    };

    window.addEventListener('scroll', debouncedScroll);
    return () => {
      window.removeEventListener('scroll', debouncedScroll);
      clearTimeout(timeoutId);
    };
  }, [handleScroll, screenType]);

  const openSidebar = (isValue: boolean) => {
    setIsSidebarOpen(isValue);
    setIsVisible(isValue);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
    setTimeout(() => setIsVisible(false), 50);
  };

  const scrollToTarget = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const updateJobchangeProcesses = async () => {
    await jobchangeProcessesRefetch();
    scrollToTarget();
  };

  const handleUpdateCandidateAssignees = useCallback(
    async (candidateProfileId: number, userIds: number[]) => {
      try {
        await updateCandidate({ candidateProfileId, organization_user_ids: userIds });
        await jobchangeProcessesRefetch();
      } catch (e) {
        showErrorToast('担当者の更新に失敗しました');
      }
    },
    [updateCandidate, jobchangeProcessesRefetch]
  );

  const onUpdateOrder = (value: string) => {
    setOrderValue(value);
    setJobChangeProcessQuery({
      ...jobChangeProcessQuery,
      order: value === '-created_at' ? '' : value,
    });
  };

  const handleSelectFromBoard = (jobChangeProcess: JobChangeProcess) => {
    setCurrentSelectjobChangeProcess(jobChangeProcess);
    openSidebar(true);
  };

  return (
    <div className="relative flex flex-col gap-md">
      {showTopBar && <div className="fixed inset-x-0 top-0 z-30 h-4 bg-gray"></div>}
      {screenType === 'list' ? (
        <>
          <div ref={searchRef} className="flex flex-col gap-md px-lg py-sm">
            <div className="flex items-center justify-end">
              <Button
                type="button"
                color="secondary"
                size="md"
                onClick={() => setScreenType('board')}
              >
                <MdAutorenew className="size-icon-xs" />
                ボードビュー
              </Button>
            </div>
            <JobChangeProcessSearchOption isOption={isOption} setIsOption={setIsOption} />
            <div className="flex flex-col gap-2">
              <div className="w-[196px]">
                <Select items={ORDER_ITEMS} value={orderValue} onValueChange={onUpdateOrder} />
              </div>
            </div>
          </div>
          <BackToSearchBar targetRef={searchRef} />

          <div className="relative">
            <div className="px-md" ref={listRef}>
              <JobChangeProcessList
                jobChangeProcesses={jobChangeProcesses}
                isLoading={isLoading}
                isFetchingNextPage={isFetchingNextPage}
                openSidebar={openSidebar}
                jobchangeProcessesRefetch={updateJobchangeProcesses}
                isSidebarOpen={isSidebarOpen}
                activeUsers={activeUsers}
                currentUserId={currentUserId}
                onUpdateCandidateAssignees={handleUpdateCandidateAssignees}
              />
            </div>
          </div>
        </>
      ) : (
        <JobChangeProcessBoardWrapper
          setScreenType={setScreenType}
          onSelect={handleSelectFromBoard}
          selectedId={currentSelectjobChangeProcess?.id}
        />
      )}
      <div>
        {isVisible && (
          <div
            ref={sidebarRef}
            className={clsx(
              'fixed top-[60px] right-0 z-50 h-[calc(100vh_-_60px)] w-[415px] overflow-hidden bg-white shadow-[0px_0px_2px_0px_rgba(0,_0,_0,_0.12)]',
              isSidebarOpen ? 'animate-slideInRight' : 'animate-fadeOutRight'
            )}
          >
            <div className="flex h-full flex-col overflow-hidden">
              <JobChangeProcessSidebar
                closeSidebar={closeSidebar}
                statusOpen={() => setLogStatusModalOpen(true)}
                isSidebarOpen={isSidebarOpen}
              />
            </div>
          </div>
        )}
        <JobChangeProcessLogStatusUpdateModal
          open={logStatusModalOpen}
          onOpenChange={setLogStatusModalOpen}
          jobchangeProcessesRefetch={updateJobchangeProcesses}
        />
      </div>
    </div>
  );
}
